// 2x pole-mounted LED ticker boards (WORLD.ads) for the art/perspective prototype.
// The housing is one static canvas (posts + board frame); the text is a separate strip that render.ts scrolls with a
// TilingSprite, so nothing is repainted per frame. Units are hd pixels (2 per world pixel), like hd-kit.ts.
// The text uses the shared 2x sign font (hd-glyphs.ts), drawn as an LED dot matrix.
import { rawCanvas } from './canvas';
import { K, NEON, type NeonColor, type G, px, dk, lt, alpha, frame } from './hd-kit';
import { GLYPHS } from './hd-glyphs';

const glyphOf = (c: string) => GLYPHS[c] ?? GLYPHS[' '];
const gw = (c: string) => glyphOf(c)[0].length;
const GAP = 1;
/** width in font pixels */
export const adTextW = (t: string) => [...t].reduce((w, c) => w + gw(c) + GAP, -GAP);

/** One LED cell is 2x2 hd px (1 world px): bright top-left pixel so the text reads as a dot matrix. */
function led(g: G, t: string, x: number, y: number, col: string) {
  let cx = x;
  for (const ch of t) {
    glyphOf(ch).forEach((r, j) => { for (let i = 0; i < r.length; i++) if (r[i] === '#') {
      g.fillStyle = col; g.fillRect(cx + i * 2, y + j * 2, 2, 2);
      g.fillStyle = lt(col, 0.55); g.fillRect(cx + i * 2, y + j * 2, 1, 1);
    } });
    cx += (gw(ch) + GAP) * 2;
  }
}

export const AD_COLORS: NeonColor[] = ['cyan', 'pink', 'amber'];
export interface HdAd {
  housing: HTMLCanvasElement;
  strip: HTMLCanvasElement;
  /** window rect inside the housing, hd px */
  win: { x: number; y: number; w: number; h: number };
  /** housing anchor (bottom centre = the posts' feet), hd px */
  ay: number;
}
export const HD_AD_W = 360;
const BOARD_H = 24, POST_H = 38, FOOT = 4;

export function buildHdAd(text: string, color: NeonColor): HdAd {
  const W = HD_AD_W, H = BOARD_H + POST_H + FOOT;
  const housing = rawCanvas(W, H);
  const g = housing.getContext('2d')!; const col = NEON[color];
  // soft ground shadow (light from the top-left, so it falls down-right)
  px(g, 6, H - 4, W - 6, 4, alpha(K.ink, 0.35));
  // two steel posts with a lit left edge and shaded right edge
  for (const x of [14, W - 22]) {
    px(g, x, BOARD_H, 8, POST_H, K.metal); px(g, x, BOARD_H, 2, POST_H, K.metalHi); px(g, x + 6, BOARD_H, 2, POST_H, K.metalLo);
    px(g, x - 2, BOARD_H + POST_H - 2, 12, 4, K.metalLo); px(g, x - 2, BOARD_H + POST_H - 2, 12, 1, K.metal);
  }
  // board: dark body, light top edge, shaded bottom edge, a thin neon trim line under it
  px(g, 0, 0, W, BOARD_H, K.ink); frame(g, 0, 0, W, BOARD_H, dk(K.slate, 0.55));
  px(g, 1, 1, W - 2, 2, K.metalHi); px(g, 1, BOARD_H - 3, W - 2, 2, K.metalLo);
  px(g, 4, BOARD_H - 1, W - 8, 1, alpha(col, 0.8));
  const win = { x: 6, y: 3, w: W - 12, h: 18 };
  px(g, win.x - 1, win.y - 1, win.w + 2, win.h + 2, K.night);
  // the strip: text on a dark LED field, plus a gap so the loop has a pause between runs
  const tw = adTextW(text) * 2 + 120;
  const strip = rawCanvas(tw, win.h);
  const s = strip.getContext('2d')!;
  px(s, 0, 0, tw, win.h, K.night);
  led(s, text, 40, 3, col);
  return { housing, strip, win, ay: H };
}
